import { useState, useEffect } from 'react';

const API_BASE = import.meta.env.VITE_API_BASE ?? 'https://life.wowerix.com';

interface BotInfo {
  username: string;
  id: number;
  name?: string;
  bot_url: string;
  qr_url: string;
}

interface StatusResponse {
  connected: boolean;
  bot?: BotInfo;
}

export function TelegramQR() {
  const [status, setStatus] = useState<'loading' | 'connected' | 'disconnected'>('loading');
  const [bot, setBot] = useState<BotInfo | null>(null);

  // Form state (chỉ dùng khi chưa kết nối)
  const [botName, setBotName] = useState('');
  const [botToken, setBotToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');

  // Load status khi mount
  useEffect(() => {
    fetchStatus();
  }, []);

  async function fetchStatus() {
    const token = localStorage.getItem('eric_ai_token');
    if (!token) { setStatus('disconnected'); return; }

    try {
      const res = await fetch(`${API_BASE}/api/telegram/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data: StatusResponse = await res.json();
      if (data.connected && data.bot) {
        setBot(data.bot);
        setStatus('connected');
      } else {
        setStatus('disconnected');
      }
    } catch {
      setStatus('disconnected');
    }
  }

  async function handleConnect() {
    if (!botToken.trim()) { setError('Vui lòng nhập Bot Token.'); return; }
    setError('');
    setConnecting(true);

    const token = localStorage.getItem('eric_ai_token');
    if (!token) { setError('Chưa đăng nhập.'); setConnecting(false); return; }

    try {
      const res = await fetch(`${API_BASE}/api/telegram/connect`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ bot_token: botToken.trim(), bot_name: botName.trim() }),
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.error ?? 'Kết nối thất bại. Kiểm tra lại token.');
        return;
      }

      setBot(data.bot);
      setStatus('connected');
      setBotToken('');
      setBotName('');
    } catch {
      setError('Lỗi mạng. Vui lòng thử lại.');
    } finally {
      setConnecting(false);
    }
  }

  async function handleDisconnect() {
    const token = localStorage.getItem('eric_ai_token');
    if (!token) return;

    try {
      await fetch(`${API_BASE}/api/telegram/disconnect`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setBot(null);
      setStatus('disconnected');
    } catch {
      setError('Lỗi khi ngắt kết nối. Thử lại.');
    }
  }

  return (
    <div style={{
      background: 'var(--bg-elevated)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-md)',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '16px 20px',
        borderBottom: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        background: 'rgba(34,158,217,0.06)',
      }}>
        <div style={{
          width: 38, height: 38, borderRadius: '50%',
          background: 'linear-gradient(135deg, #229ED9, #1a7ab5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '20px', flexShrink: 0,
        }}>✈️</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--text-primary)' }}>
            Telegram Bot
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
            Kết nối bot Telegram cá nhân với Erix Life
          </div>
        </div>
        {status === 'connected' && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            background: 'rgba(16,185,129,0.15)',
            border: '1px solid rgba(16,185,129,0.3)',
            borderRadius: '999px',
            padding: '3px 10px',
            fontSize: '11px', color: '#10b981', fontWeight: 600,
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981', display: 'inline-block' }} />
            Đã kết nối
          </div>
        )}
      </div>

      <div style={{ padding: '20px' }}>
        {/* Loading */}
        {status === 'loading' && (
          <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)', fontSize: '13px' }}>
            <div style={{ fontSize: '20px', marginBottom: '8px' }}>⏳</div>
            Đang kiểm tra trạng thái...
          </div>
        )}

        {/* Disconnected — Form nhập token */}
        {status === 'disconnected' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {/* Hướng dẫn nhanh */}
            <div style={{
              background: 'rgba(34,158,217,0.07)',
              border: '1px solid rgba(34,158,217,0.15)',
              borderRadius: '10px',
              padding: '12px 14px',
              fontSize: '12px',
              color: 'var(--text-secondary)',
              lineHeight: 1.7,
            }}>
              <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px' }}>
                📋 Cách tạo bot Telegram:
              </div>
              1. Mở Telegram → tìm <code style={{ color: '#229ED9', background: 'rgba(34,158,217,0.1)', padding: '1px 5px', borderRadius: '4px' }}>@BotFather</code><br />
              2. Gửi <code style={{ color: '#229ED9', background: 'rgba(34,158,217,0.1)', padding: '1px 5px', borderRadius: '4px' }}>/newbot</code> và đặt tên bot<br />
              3. Copy Token nhận được → Dán vào ô bên dưới → Nhấn Kết nối
            </div>

            {/* Input: Bot Name (optional) */}
            <div>
              <label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: '6px' }}>
                Tên bot (tuỳ chọn)
              </label>
              <input
                className="input"
                value={botName}
                onChange={e => setBotName(e.target.value)}
                placeholder="vd: MyAssistantBot"
                style={{ width: '100%', fontSize: '13px', boxSizing: 'border-box' }}
              />
            </div>

            {/* Input: Bot Token */}
            <div>
              <label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: '6px' }}>
                Bot Token <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showToken ? 'text' : 'password'}
                  className="input"
                  value={botToken}
                  onChange={e => { setBotToken(e.target.value); setError(''); }}
                  onKeyDown={e => e.key === 'Enter' && handleConnect()}
                  placeholder="123456789:ABC-DEF1234ghIkl-zyx57W2v1u123ew11"
                  style={{ width: '100%', fontSize: '12px', fontFamily: 'var(--font-mono)', paddingRight: '42px', boxSizing: 'border-box' }}
                  autoComplete="off"
                />
                <button
                  onClick={() => setShowToken(v => !v)}
                  style={{
                    position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--text-muted)', fontSize: '14px', padding: '0',
                  }}
                  title={showToken ? 'Ẩn token' : 'Hiện token'}
                >
                  {showToken ? '🙈' : '👁'}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div style={{
                background: 'rgba(239,68,68,0.1)',
                border: '1px solid rgba(239,68,68,0.3)',
                borderRadius: '8px',
                padding: '10px 12px',
                fontSize: '12px',
                color: '#ef4444',
              }}>
                ❌ {error}
              </div>
            )}

            {/* Connect button */}
            <button
              className="btn btn--primary"
              onClick={handleConnect}
              disabled={connecting || !botToken.trim()}
              style={{
                background: connecting ? 'var(--bg-tertiary)' : 'linear-gradient(135deg, #229ED9, #1a7ab5)',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: connecting ? 'not-allowed' : 'pointer',
                width: '100%',
              }}
            >
              {connecting
                ? '⏳ Đang kết nối...'
                : '🔗 Kết nối Bot Telegram'}
            </button>
          </div>
        )}

        {/* Connected — Hiện QR code */}
        {status === 'connected' && bot && (
          <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
            {/* QR Code */}
            <div style={{
              flexShrink: 0,
              background: '#ffffff',
              borderRadius: '12px',
              padding: '8px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
            }}>
              <img
                src={bot.qr_url}
                alt={`QR code for t.me/${bot.username}`}
                width={160} height={160}
                style={{ display: 'block', borderRadius: '6px' }}
                loading="lazy"
              />
            </div>

            {/* Info */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Bot URL */}
              <div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, marginBottom: '4px' }}>
                  Bot URL
                </div>
                <a
                  href={bot.bot_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#229ED9', fontFamily: 'var(--font-mono)', fontSize: '14px', fontWeight: 700, textDecoration: 'none' }}
                >
                  t.me/{bot.username}
                </a>
              </div>

              {/* Instruction */}
              <div style={{
                background: 'rgba(34,158,217,0.07)',
                border: '1px solid rgba(34,158,217,0.15)',
                borderRadius: '8px',
                padding: '10px 12px',
                fontSize: '12px',
                color: 'var(--text-secondary)',
                lineHeight: 1.7,
              }}>
                📱 <strong>Quét QR</strong> hoặc mở link để chat với bot.<br />
                Gửi <code style={{ color: '#229ED9' }}>/start</code> → bắt đầu chat AI ngay!
              </div>

              {/* Commands hint */}
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', lineHeight: 1.8 }}>
                <div style={{ fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '2px' }}>Lệnh hỗ trợ:</div>
                <code>/start</code> — Bắt đầu &nbsp;|&nbsp;
                <code>/help</code> — Trợ giúp &nbsp;|&nbsp;
                <code>/clear</code> — Xóa lịch sử
              </div>

              {/* Disconnect */}
              <button
                className="btn btn--secondary btn--sm"
                onClick={handleDisconnect}
                style={{
                  color: '#ef4444',
                  borderColor: 'rgba(239,68,68,0.3)',
                  fontSize: '12px',
                  alignSelf: 'flex-start',
                }}
              >
                🔌 Ngắt kết nối bot
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
